import { css, keyframes } from "@emotion/react";
import React, { forwardRef } from "react";
import { FieldGroupContextValue, useFieldGroup } from "../FieldGroup";
import { ShimmerEffect } from "../ShimmerEffect";
import { Theme, useTheme } from "../theme";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import { useClickAnimation } from "../hooks/useClickAnimation";
import { useRadioButtonGroup } from "./RadioButtonGroup";

const fade = keyframes`
  100% {
      opacity: 0;
  }
`;
const wave = keyframes`
  100% {
      box-shadow: 0 0 0 6px currentcolor;
  }
`;

export interface RadioButtonProps
  extends React.InputHTMLAttributes<HTMLInputElement>, FieldGroupContextValue {
  /**
   * 子要素は文字列である必要があります。
   */
  children: string;
  checkedValue?: string;
  checked?: never;
  /**
   * テーマカラーを指定できます。
   */
  color?: keyof Theme["palette"]["intentions"];
  onClick?: (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => void;
  /**
   * 値を指定してください。
   */
  value: string;
  className?: string;
  style?: React.CSSProperties;
  /**
   * アンコントロールにする場合はRadioButtonにdefaultCheckedを渡し、RadioButtonGroupにvalueを渡さないようにしてください。
   */
  defaultChecked?: boolean;
}

export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  (
    {
      color = "primary",
      checkedValue: checkedValueProp,
      value,
      style,
      className,
      children,
      onClick: propsOnClick,
      loading: propsLoading,
      readOnly: propsReadOnly,
      disabled,
      name: nameProp,
      onChange: onChangeProp,
      ...props
    },
    ref,
  ) => {
    const group = useRadioButtonGroup();
    const name = nameProp ?? group?.name;
    const checkedValue = checkedValueProp ?? group?.checkedValue;
    const groupOnChange = group?.onChange;
    const theme = useTheme();
    const { clicked, onClick, onAnimationEnd } =
      useClickAnimation(propsOnClick);
    const { loading, readOnly } = useFieldGroup({
      loading: propsLoading,
      readOnly: propsReadOnly,
    });
    return (
      <ShimmerEffect active={loading}>
        <label
          data-clicked={clicked}
          style={style}
          className={className}
          css={[
            theme.typography.button,
            css`
              position: relative;
              box-sizing: border-box;
              display: inline-flex;
              flex-shrink: 0;
              align-items: center;
              justify-content: center;
              min-height: 42px;
              padding: ${theme.spacing / 2}px ${theme.spacing * 2.5}px;
              margin: 0px;
              color: ${theme.palette.intentions[color].main};
              touch-action: manipulation;
              cursor: pointer;
              user-select: none;
              background-color: white;
              border: 1px solid ${theme.palette.intentions[color].main};
              border-radius: 4px;
              outline: none;
              box-shadow: ${theme.shadows[1]};
              transition: ${theme.transitions.duration.shortest}
                ${theme.transitions.easing.easeIn};
              appearance: none;
              &:hover {
                box-shadow: ${theme.shadows[3]};
              }

              &::after {
                position: absolute;
                top: -1px;
                right: -1px;
                bottom: -1px;
                left: -1px;
                z-index: 1;
                display: block;
                color: ${theme.palette.intentions[color].main};
                pointer-events: none;
                content: "";
                border-radius: inherit;
                opacity: 0.2;
                transform: scale(1);
              }
              &[data-clicked="true"]::after {
                animation:
                  ${fade} 2s cubic-bezier(0.08, 0.82, 0.17, 1),
                  ${wave} 0.4s cubic-bezier(0.08, 0.82, 0.17, 1);
              }
              &:has(input:disabled) {
                color: ${theme.palette.action.disabled};
                text-shadow: none;
                cursor: not-allowed;
                border-color: ${theme.palette.action.disabledBackground};
                box-shadow: none;
              }
              &:has(input:checked) {
                color: ${theme.palette.intentions[color].contrastText};
                background-color: ${theme.palette.intentions[color].main};
                border-color: ${theme.palette.intentions[color].main};
              }
              &:has(input:checked:active) {
                background-color: ${theme.palette.intentions[color].dark};
                border-color: ${theme.palette.intentions[color].dark};
              }
              &:has(input:checked:disabled) {
                color: ${theme.palette.action.disabled};
                background-color: ${theme.palette.action.disabledBackground};
                border-color: ${theme.palette.action.disabledBackground};
              }
            `,
          ]}
        >
          <VisuallyHidden>
            <input
              key={`${loading}`}
              ref={ref}
              type="radio"
              name={name}
              onClick={onClick}
              onChange={onChangeProp ?? groupOnChange}
              value={value}
              onAnimationEnd={onAnimationEnd}
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              disabled={disabled || readOnly}
              checked={checkedValue ? checkedValue === value : undefined}
              {...props}
            />
          </VisuallyHidden>
          {children}
        </label>
      </ShimmerEffect>
    );
  },
);
